import * as THREE from 'three';
import { drawCircleFromCode } from '../../../commands/drawCircleFromCode';
import { useDispatch } from 'react-redux';
import { setCommandType, setOperationData, resetOperation } from '../../../redux/operationSlice';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { createTextSprite } from '../../../utils/createTextSprite'; // zaten sizde var
import { findAOSForCable, findCableByCurrent, findKAKRByCurrent } from '../../../electrical/electricalFunctions';
import { current } from '@reduxjs/toolkit';

// 🆕 simpleSupply import'u
import { addComponent } from '../../../redux/simpleSupplySlice';
import { COMPONENT_KINDS } from '../../../types/SimpleSupplyPoint';
import { updatePanelInputWire } from '../../../modules/wiring';
import { getObjectById } from '../../../utils/getObjectById';

export function usePanelModify(scene) {
    const dispatch = useDispatch();

    function findPanelById(panelId) {
        if (!scene) return null;
        return getObjectById(scene, panelId);
    }

    function updateGeometry(panel, outCount, width, symmetricalOffset) {
        const dir = panel.userData.dir
        // MainPanel SABİT boyutludur (child'ları 'mainPanel-outer'/'mainPanel-hatch1/2',
        // height=50). Buradaki resize/height/offsetGroup.position mutasyonu mainPanel'i
        // bozar → ESC sonrası giriş (ön) kenarı açılıyordu. updatePanelSize (revert yolu)
        // zaten mainPanel'i atlıyor (panelExitManager:538); ADD yolu da atlamalı —
        // aksi halde iki yol tutarsız kalıyor. Yalnızca giriş hattını güncelle.
        if (panel?.userData?.type === 'mainPanel') {
            if (scene) updatePanelInputWire(scene, panel);
            return;
        }
        const offsetGroup = panel.children.find(child => child.name === 'offsetGroup');
        const polyline = offsetGroup?.children.find(child => child.name === 'panel-outer');
        const hatch = offsetGroup?.children.find(child => child.name === 'panel-hatch');
        
        // ✅ Mevcut height'ı kaydet (updatePanelInputWire için)
        panel.userData.oldHeight = panel.userData.height;

        let height = (outCount * 5 + 10);
        // Defansif: outSnaps array'i veya içindeki obje/userData undefined
        // olabilir (özellikle JBZ load sonrası scene restore'da).
        const outSnaps = Array.isArray(panel.userData?.outSnaps) ? panel.userData.outSnaps : [];
        const rezS = outSnaps.filter((obj) => (obj?.userData?.orderNo ?? 0) > 0).length
        const rezE = outSnaps.filter((obj) => (obj?.userData?.orderNo ?? 0) < 0).length
        const dif = rezS - rezE
        let startH = rezS * 5
        let endH = rezE * 5

        if (panel.userData.endH >= endH) {
            endH = panel.userData.endH
        } else {
            endH = rezE * 5
            offsetGroup.position.y -= 2.5;
            panel.userData.endH = endH
        }
        if (panel.userData.startH >= startH) {
            startH = panel.userData.startH
        } else {
            startH = rezS * 5
            panel.userData.startH = startH
            offsetGroup.position.y += 2.5;
        }


        if (panel.userData.height >= startH + endH) {
            height = panel.userData.height
        } else {
            height = startH + endH
        }
        
        // ✅ Yeni height'ı güncelle
        panel.userData.height = height;

        if (polyline && polyline.geometry) {
            polyline.geometry.setFromPoints([
                new THREE.Vector3(-width / 2 + symmetricalOffset * dir, -height / 2, 0),
                new THREE.Vector3(-width / 2 + symmetricalOffset * dir, height / 2, 0),
                new THREE.Vector3(width / 2 + symmetricalOffset * dir, height / 2, 0),
                new THREE.Vector3(width / 2 + symmetricalOffset * dir, -height / 2, 0),
            ]);
            polyline.geometry.attributes.position.needsUpdate = true;
        }

        if (hatch && hatch.geometry) {
            // Vertex formülü createPanel.js hatchPaths ile aynı olmalı:
            // x = ±width/2 + symmetricalOffset * dir   (offset ayrı çarpılır, hepsi değil).
            // Aksi halde dir=-1 durumunda üçgen ters tarafa çevrilip görsel olarak
            // siliniyormuş gibi görünür.
            const vertices = [
                new THREE.Vector3(-width / 2 + symmetricalOffset * dir, height / 2, 0),
                new THREE.Vector3(width / 2 + symmetricalOffset * dir, -height / 2, 0),
                new THREE.Vector3(-width / 2 + symmetricalOffset * dir, -height / 2, 0),
            ];
            hatch.geometry.setFromPoints(vertices);
            hatch.geometry.attributes.position.needsUpdate = true;
        }

        // ✅ Panel giriş hattını güncelle
        if (scene) {
            updatePanelInputWire(scene, panel);
        }
    }

    function updatePanel(panel) { 
        if (!panel) return
        const outs = panel.outs
        let installedPower = 0, demandPower = 0, akim = 0, fazSayi = 3, faz;

        if (panel.details.faz.toLowerCase() === "rst") {
            fazSayi = 3
        } else {
            fazSayi = 1
        }

        outs.forEach((out) => {
            installedPower += out.power
        })

        if (panel.details.purposeOfUSe === "Mesken") {
            if (panel.details.demandFactor === "60-40%") {
                if (installedPower > 8000) {
                    demandPower = 4800 + (installedPower - 8000) * 0.4
                } else {
                    demandPower = installedPower * 0.6
                }
            } else if (panel.details.demandFactor === "100%") {
                demandPower = installedPower
            }
        } else if (panel.details.purposeOfUSe === "Müşterek") {
            if (panel.details.demandFactor === "60-40%") {
                if (installedPower > 8000) {
                    demandPower = 4800 + (installedPower - 8000) * 0.4
                } else {
                    demandPower = installedPower * 0.6
                }
            } else if (panel.details.demandFactor === "100%") {
                demandPower = installedPower
            }
        } else if (panel.details.purposeOfUSe === "İşYeri") {
            if (panel.details.demandFactor === "100-50%") {
                if (installedPower > 8000) {
                    demandPower = 4800 + (installedPower - 8000) * 0.4
                } else {
                    demandPower = installedPower * 0.6
                }
            } else if (panel.details.demandFactor === "100%") {
                demandPower = installedPower
            }
        } else if (panel.details.purposeOfUSe === "Asansör") {
            if (panel.details.demandFactor === "55%") {
                demandPower = installedPower * 0.55
            }

        }

        if (fazSayi === 3) {
            akim = demandPower / 526
        } else {
            akim = installedPower / 220
        }
        panel.details.installedPower = installedPower
        panel.details.demandPower = demandPower
        panel.details.akim = akim
        //-------------------------
        const CableType=panel.salt.kolon.type
        const iletken = findCableByCurrent(akim, 5, "airCurCap", 6, 10,"NYY")
  
        
        let currentCaryCap=0
        if(iletken.type==="NYY"){currentCaryCap=iletken.earthCurCap}
        if(iletken.type==="NYM"){currentCaryCap=iletken.airCurCap} 
        const kesici = findAOSForCable(currentCaryCap, akim, 4, 25, "B", 10, 0)//(current,I0,phaseCount,minI0,type,Ith,tolerans)

        
        const SayacKesici = findAOSForCable(currentCaryCap, akim, 4, 25, "C", 10, 0)//(current,I0,phaseCount,minI0,type,Ith,tolerans)
        const KAKR = findKAKRByCurrent(kesici.Akim, fazSayi + 1, 30, "A")

        
        panel.salt.kolon = iletken
        panel.salt.kesici = kesici
        panel.salt.SayacKesici = SayacKesici
        panel.salt.KAKR = KAKR
 
        return panel

    }

    function setCount(panelId, nodeId, housingId, newCount, options, autoStart = true) {
         const panel = findPanelById(panelId);
        if (!panel) return;
        const lastOutID = generateUniqueId("outSnap");
        const location = options.addPosition;
        const command = options.command;
        const dir = panel.userData.dir
        const symmetricalOffset = panel.userData.symmetricalOffset;
        const basePoint = panel.userData.basePoint;
        let titleNo = 1
        let outSnapGroup = panel.children.find(child => child.name === 'outSnapGroup');
        if (!outSnapGroup) {
            outSnapGroup = new THREE.Group();
            outSnapGroup.name = 'outSnapGroup';
            outSnapGroup.userData.StartSnapsCount = 0;
            outSnapGroup.userData.EndSnapsCount = 0;
            panel.add(outSnapGroup);
        }
         // LOAD sonrası functional setup: panel.userData.outSnaps mesh referansları
        // JSON round-trip'te kaybolur (snapshot Object3D ref'lerini strip eder), AMA
        // outSnapGroup child'ları (orderNo'lu panelCon_out mesh'leri) snapshot'tan
        // restore edilir. Ref boş ama child varsa yeniden kur; ayrıca StartSnapsCount/
        // EndSnapsCount'u orderNo'lardan tazele. Aksi halde updateGeometry offset
        // (rezS/rezE) ve yeni çıkışın orderNo'su sıfırlanıp eski çıkışların panelWire
        // ilk kenarı kayıttan açılan projede yanlış ötelenir.
        if ((!panel.userData.outSnaps || panel.userData.outSnaps.length === 0)
            && outSnapGroup.children.length > 0) {
            const snaps = outSnapGroup.children.filter(o => o.userData?.type === 'panelCon_out');
            if (snaps.length > 0) {
                panel.userData.outSnaps = snaps;
                outSnapGroup.userData.StartSnapsCount = snaps.filter(o => (o.userData?.orderNo ?? 0) > 0).length;
                outSnapGroup.userData.EndSnapsCount = snaps.filter(o => (o.userData?.orderNo ?? 0) < 0).length;
            }
        }
        let OutSnapGroupStartSnapsCount = outSnapGroup.userData.StartSnapsCount;
        let OutSnapGroupEndSnapsCount = outSnapGroup.userData.EndSnapsCount;
        const currentOuts = [...(panel.userData.outSnaps || [])];
        let lastOut = null;
        if (location === 'start') {
            outSnapGroup.children.forEach(snap => {
                snap.position.y += 5;
                snap.position.y *= dir;
            });

            const newOut = drawCircleFromCode(scene, {
                center: { x: 0, y: 0, z: 0 },
                radius: 2,
                color: 0xff0000,
            });
            newOut.userData = {
                type: 'panelCon_out',
                panelID: panelId,
                no: 1,
                outNo: 1,
                isSelectable: true,
                id: lastOutID,
                orderNo: OutSnapGroupStartSnapsCount + 1

            };
            newOut.visible = false;
            outSnapGroup.userData.StartSnapsCount = OutSnapGroupStartSnapsCount + 1;
            outSnapGroup.add(newOut);
            panel.userData.outSnaps = [newOut, ...currentOuts];
            lastOut = newOut;

        } else if (location === 'end') {
            titleNo = newCount
            const newOut = drawCircleFromCode(scene, {
                center: { x: 0, y: 5, z: 0 },
                radius: 2,
                color: 0xff0000,
            });
            newOut.userData = {
                type: 'panelCon_out',
                panelID: panelId,
                no: newCount,
                outNo: newCount,
                isSelectable: true,
                id: lastOutID,
                orderNo: -(OutSnapGroupEndSnapsCount + 1)
            };
            newOut.visible = false;
            outSnapGroup.userData.EndSnapsCount = OutSnapGroupEndSnapsCount + 1;
            outSnapGroup.add(newOut);
            panel.userData.outSnaps = [...currentOuts, newOut];
            lastOut = newOut;

        }
        const width = 15;
        const height = 25;
        updateGeometry(panel, newCount, width, symmetricalOffset, location);
        if (autoStart && lastOut) {
            const initialPoint = {
                x: basePoint.x + (symmetricalOffset * dir),
                y: basePoint.y + (location === 'start'
                    ? (outSnapGroup.userData.StartSnapsCount - 1) * 5
                    : -(outSnapGroup.userData.EndSnapsCount) * 5),
            };
            
            const operationPayload = {
                initialPoint,
                addPosition: location, // 'start' veya 'end' bilgisini ekle
                direction: dir, // Panel direction
                initialItemType: options.initialItemType, // ✅ Pass initialItemType
                source: {
                    type: 'panelConnection',
                    panelID: panelId,
                    masterNodeId: nodeId,
                    housingId: housingId,
                    no: titleNo,
                    outNo: titleNo,
                    UUID: lastOut.uuid,
                    OutID: lastOutID,
                    phaseCount: options.phaseCount ?? 3,
                    purposeOfUse: options.purposeOfUse,
                    aboneTuru: options.aboneTuru,
                    name: options.name
                },
            };

            dispatch(resetOperation());
            dispatch(setCommandType(command));
            dispatch(setOperationData(operationPayload));
        }
        if (location === "start")
            renumberOutSnaps(panel);
    }



    function renumberOutSnaps(panel) {
        const outSnapGroup = panel.children.find(child => child.name === 'outSnapGroup');
        if (!outSnapGroup) return;

        const sortedOuts = Array.isArray(panel.userData?.outSnaps) ? panel.userData.outSnaps : [];
        if (sortedOuts.length === 0) return;

        // Tek scene.traverse ile tum ilgili label'lari outSnapID -> label map'ine topla
        const labelMap = new Map();
        scene.traverse((obj) => {
            if (obj.name === "titleLabel" && obj.userData.outSnapID) {
                labelMap.set(obj.userData.outSnapID, obj);
            }
        });

        sortedOuts.forEach((snap, index) => {
            if (!snap?.userData) return; // defansif -- restore sonrasi eksik ref olabilir
            snap.userData.no = sortedOuts.length - index;
            snap.userData.outNo = index + 1;

            const label = labelMap.get(snap.userData.id);
            if (label) {
                const localPos = label.position.clone();
                const newLabel = createTextSprite(`${snap.userData.outNo}`, {
                    fontSize: 120,
                    color: '#00ffff',
                    backgroundColor: 'transparent',
                    strokeColor: '#00ffff',
                    padding: 8,
                });
                newLabel.name = 'titleLabel';
                newLabel.userData.outSnapID = snap.userData.id;
                newLabel.position.copy(localPos);

                const parent = label.parent;
                if (parent) {
                    parent.remove(label);
                    parent.add(newLabel);
                }

                label.material?.map?.dispose?.();
                label.material?.dispose?.();
                label.geometry?.dispose?.();
            }
        });
        panel.userData.outSnaps = sortedOuts;
    }


    return { setCount, updatePanel };
}